import type { DraftRecording } from '@/types'
import { db } from './database'
import { hydrateBlob, serializeBlobField } from './blob'
import { runDbOperation } from './errors'
import {
  isStoragePressureError,
  logSyncTelemetry,
  toTelemetryErrorFields,
} from '../sync/telemetry'

function hydrateDraftBlob(draft: DraftRecording): void {
  if (!draft.blob && draft.blobData) {
    draft.blob = hydrateBlob(draft.blobData, draft.blobType) as Blob
  }
}

export async function getCurrentDraft(): Promise<DraftRecording | null> {
  const draft = (await db.drafts.get('current')) ?? null
  if (draft) {
    hydrateDraftBlob(draft)
  }
  return draft
}

export async function getAllDrafts(): Promise<DraftRecording[]> {
  return runDbOperation(async () => {
    const drafts = await db.drafts.toArray()
    for (const draft of drafts) {
      hydrateDraftBlob(draft)
    }
    return drafts
  })
}

export async function saveDraft(draft: DraftRecording): Promise<void> {
  return runDbOperation(async () => {
    try {
      const toStore: DraftRecording = { ...draft }
      const { data, type } = await serializeBlobField(draft.blob, draft.blobType)
      toStore.blob = null as any
      toStore.blobData = data
      toStore.blobType = type
      await db.drafts.put(toStore)
    } catch (error) {
      if (isStoragePressureError(error)) {
        logSyncTelemetry({
          type: 'storage-pressure',
          scope: 'drafts',
          operation: 'saveDraft',
          itemId: draft.id,
          ...toTelemetryErrorFields(error),
        })
      }
      throw error
    }
  })
}

export async function deleteDraft(id: string = 'current'): Promise<void> {
  return runDbOperation(async () => {
    await db.drafts.delete(id)
  })
}

export async function clearAllDrafts(): Promise<void> {
  await db.drafts.clear()
}
